import {
    Alert,
    Box,
    Button,
    ColumnLayout,
    DatePicker,
    FormField,
    Input,
    Modal,
    Select,
    SpaceBetween,
    Textarea,
    Tiles,
    Toggle,
    TokenGroup,
} from '@amzn/awsui-components-react';
import React, { useState } from 'react';
import CustomTag from 'src/models/accounting-calendar/CustomTag';
import {
    DateType,
    Recipients,
    RecurringFrequency,
} from 'src/models/accounting-calendar/CustomTagEnums';
import { MONTH_OPTIONS, buildYearOptions, formatExcludedMonth } from 'src/utils/accountingCalendarUtils';

// DatePicker emits/accepts YYYY/MM/DD; the API uses YYYY-MM-DD.
const toApiDate = (d: string): string => (d ? d.replace(/\//g, '-') : d);
const toPickerDate = (d?: string): string => (d ? d.replace(/-/g, '/') : '');

const toOption = (v?: string) => (v ? { label: v, value: v } : null);

// Accounting-date offsets shown in the dropdown. Negative values mean N working days BEFORE
// MEC close (WD-N); positive values mean N working days AFTER start of month (WD+N). 0 is
// intentionally excluded — the backend regex (CustomTagValidator.ACCOUNTING_DATE_PATTERN)
// rejects WD+0 / WD-0.
const WORKDAY_OFFSET_OPTIONS: { label: string; value: string }[] = [
    ...Array.from({ length: 24 }, (_, i) => 24 - i).map(n => ({
        // -24 → -1, shown as "Day -N" (value stays WD-N wire format)
        label: `Day -${n}`,
        value: String(-n),
    })),
    ...Array.from({ length: 24 }, (_, i) => i + 1).map(n => ({
        // +1 → +24, shown as "Day N"
        label: `Day ${n}`,
        value: String(n),
    })),
];

// Build the wire-format dateValue (e.g. "WD+10", "WD-24") from a signed integer.
const offsetToExpr = (offset: number): string =>
    `WD${offset > 0 ? '+' : ''}${offset}`;

// Parse a "WD±N" expression back to a signed integer string, or null if malformed/out of options.
const exprToOffset = (expr: string): string | null => {
    const m = /^WD([+-]\d+)$/.exec(expr || '');
    if (!m) {
        return null;
    }
    return m[1].startsWith('+') ? m[1].slice(1) : m[1];
};

interface Props {
    mode: 'create' | 'edit';
    initialTag?: CustomTag | null;
    submitting: boolean;
    /** Server-side error from the last submit attempt (e.g. backend validation), shown in the form. */
    submitError?: string;
    onSubmit: (tag: CustomTag) => void;
    onClose: () => void;
}

export default function CustomTagForm(props: Props) {
    const { mode, initialTag, submitting, submitError, onSubmit, onClose } = props;

    const [isRecurring, setIsRecurring] = useState<boolean>(initialTag?.isRecurring ?? false);
    const [dateType, setDateType] = useState<DateType>(initialTag?.dateType ?? DateType.CALENDAR_DATE);
    const [dateValue, setDateValue] = useState<string>(initialTag?.dateValue ?? '');
    const [tagEventName, setTagEventName] = useState<string>(initialTag?.tagEventName ?? '');
    const [description, setDescription] = useState<string>(initialTag?.description ?? '');
    const [recipients, setRecipients] = useState<Recipients | ''>(initialTag?.recipients ?? '');
    const [recurringFrequency, setRecurringFrequency] = useState<RecurringFrequency | ''>(initialTag?.recurringFrequency ?? '');
    const [recurrenceStartDate, setRecurrenceStartDate] = useState<string>(toPickerDate(initialTag?.recurrenceStartDate));
    const [recurrenceEndDate, setRecurrenceEndDate] = useState<string>(toPickerDate(initialTag?.recurrenceEndDate));
    const [excludedMonths, setExcludedMonths] = useState<string[]>(initialTag?.excludedMonths ?? []);
    const [excludedYear, setExcludedYear] = useState<string>('');
    const [excludedMonth, setExcludedMonth] = useState<string>('');
    const [error, setError] = useState<string>('');

    // Computed per render so "now + 10 years" tracks the current date.
    const yearOptions = buildYearOptions();

    function addExcludedMonth() {
        if (!excludedYear || !excludedMonth) {
            setError('Select both a year and a month to exclude');
            return;
        }
        const ym = `${excludedYear}-${excludedMonth}`;
        if (excludedMonths.includes(ym)) {
            return; // already added — no duplicates
        }
        setError('');
        setExcludedMonths([...excludedMonths, ym].sort());
        setExcludedYear('');
        setExcludedMonth('');
    }

    function removeExcludedMonth(index: number) {
        setExcludedMonths(excludedMonths.filter((_, i) => i !== index));
    }

    function handleSubmit() {
        setError('');
        const tag: CustomTag = {
            ...(initialTag ?? {}),
            isRecurring,
            dateType,
            dateValue: dateType === DateType.ACCOUNTING_DATE ? dateValue : toApiDate(dateValue),
            tagEventName: tagEventName.trim(),
            description: description.trim() || undefined,
            recipients: recipients as Recipients,
            recurringFrequency: isRecurring ? (recurringFrequency as RecurringFrequency) : undefined,
            recurrenceStartDate: isRecurring ? toApiDate(recurrenceStartDate) : undefined,
            recurrenceEndDate: isRecurring && recurrenceEndDate ? toApiDate(recurrenceEndDate) : undefined,
            excludedMonths: isRecurring && excludedMonths.length > 0 ? excludedMonths : undefined,
        };
        onSubmit(tag);
    }

    return (
        <Modal
            data-testid="customTagForm"
            visible={true}
            header={mode === 'create' ? 'Schedule activity' : 'Edit tag'}
            size="medium"
            onDismiss={onClose}
            footer={
                <Box float="right">
                    <Button variant="link" className="cancelButton" disabled={submitting} onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="saveButton" disabled={submitting} onClick={handleSubmit}>
                        {submitting ? 'Saving...' : (mode === 'create' ? 'Create' : 'Save')}
                    </Button>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                {(error || submitError) && <Alert type="error" data-testid="formError">{error || submitError}</Alert>}

                <FormField label="Event name">
                    <Input value={tagEventName} onChange={e => setTagEventName(e.detail.value)} />
                </FormField>

                <FormField label="Description (optional)">
                    <Textarea value={description} onChange={e => setDescription(e.detail.value)} />
                </FormField>

                <FormField label="Recipients">
                    <Select
                        selectedOption={toOption(recipients)}
                        options={Object.values(Recipients).map(r => ({ label: r, value: r }))}
                        onChange={({ detail }) => setRecipients(detail.selectedOption.value as Recipients)}
                        placeholder="Select recipients"
                    />
                </FormField>

                <FormField
                    label="Recurring"
                    description={mode === 'edit'
                        ? 'Cannot be changed after creation. Delete and recreate the tag to switch between one-time and recurring.'
                        : undefined}
                >
                    <Toggle
                        checked={isRecurring}
                        disabled={mode === 'edit'}
                        onChange={e => {
                            setIsRecurring(e.detail.checked);
                            // One-time tags must be CALENDAR_DATE.
                            if (!e.detail.checked) {
                                setDateType(DateType.CALENDAR_DATE);
                            }
                        }}
                    >
                        This is a recurring tag
                    </Toggle>
                </FormField>

                {isRecurring && (
                    <FormField label="Date type">
                        <Tiles
                            value={dateType}
                            items={[
                                {
                                    label: 'Calendar date',
                                    description: 'A specific date without MEC conversion',
                                    value: DateType.CALENDAR_DATE,
                                },
                                {
                                    label: 'Accounting date',
                                    description: 'MEC-relative date, e.g. Day N',
                                    value: DateType.ACCOUNTING_DATE,
                                },
                            ]}
                            columns={2}
                            onChange={({ detail }) => setDateType(detail.value as DateType)}
                        />
                    </FormField>
                )}

                <FormField
                    label={dateType === DateType.ACCOUNTING_DATE ? 'Accounting date' : 'Date'}
                    description={dateType === DateType.ACCOUNTING_DATE
                        ? 'Working days relative to MEC close. Negative = before close (Day -N), positive = after start of month (Day N).'
                        : (mode === 'edit' && !isRecurring
                            ? 'Date cannot be changed on a one-time tag. Delete and recreate the tag for a different date.'
                            : undefined)}
                >
                    {dateType === DateType.ACCOUNTING_DATE ? (() => {
                        const currentOffset = exprToOffset(dateValue);
                        const selected = currentOffset
                            ? WORKDAY_OFFSET_OPTIONS.find(o => o.value === currentOffset) ?? null
                            : null;
                        return (
                            <Select
                                selectedOption={selected}
                                options={WORKDAY_OFFSET_OPTIONS}
                                disabled={mode === 'edit' && !isRecurring}
                                onChange={({ detail }) => {
                                    const offset = Number(detail.selectedOption.value);
                                    setDateValue(offsetToExpr(offset));
                                }}
                                placeholder="Select working-day offset"
                            />
                        );
                    })() : (
                        <DatePicker
                            value={toPickerDate(toApiDate(dateValue))}
                            disabled={mode === 'edit' && !isRecurring}
                            onChange={({ detail }) => setDateValue(detail.value)}
                            placeholder="YYYY/MM/DD"
                            nextMonthAriaLabel="Next month"
                            previousMonthAriaLabel="Previous month"
                            todayAriaLabel="Today"
                        />
                    )}
                </FormField>

                {isRecurring && (
                    <React.Fragment>
                        <FormField label="Frequency">
                            <Select
                                selectedOption={toOption(recurringFrequency)}
                                options={Object.values(RecurringFrequency).map(f => ({ label: f, value: f }))}
                                onChange={({ detail }) => setRecurringFrequency(detail.selectedOption.value as RecurringFrequency)}
                                placeholder="Select frequency"
                            />
                        </FormField>
                        <FormField label="Recurrence start date">
                            <DatePicker
                                value={recurrenceStartDate}
                                onChange={({ detail }) => setRecurrenceStartDate(detail.value)}
                                placeholder="YYYY/MM/DD"
                                nextMonthAriaLabel="Next month"
                                previousMonthAriaLabel="Previous month"
                                todayAriaLabel="Today"
                            />
                        </FormField>
                        <FormField label="Recurrence end date (optional)">
                            <DatePicker
                                value={recurrenceEndDate}
                                onChange={({ detail }) => setRecurrenceEndDate(detail.value)}
                                placeholder="YYYY/MM/DD"
                                nextMonthAriaLabel="Next month"
                                previousMonthAriaLabel="Previous month"
                                todayAriaLabel="Today"
                            />
                        </FormField>
                        <FormField
                            label="Excluded months (optional)"
                            description="Months in which this recurring tag should be skipped."
                        >
                            <SpaceBetween direction="vertical" size="xs">
                                <ColumnLayout columns={3}>
                                    <Select
                                        selectedOption={excludedMonth
                                            ? { label: MONTH_OPTIONS[Number(excludedMonth) - 1].label, value: excludedMonth }
                                            : null}
                                        options={MONTH_OPTIONS}
                                        onChange={({ detail }) => setExcludedMonth(detail.selectedOption.value as string)}
                                        placeholder="Month"
                                    />
                                    <Select
                                        selectedOption={toOption(excludedYear)}
                                        options={yearOptions}
                                        onChange={({ detail }) => setExcludedYear(detail.selectedOption.value as string)}
                                        placeholder="Year"
                                    />
                                    <Button
                                        className="addExcludedMonthButton"
                                        onClick={addExcludedMonth}
                                        disabled={!excludedYear || !excludedMonth}
                                    >
                                        Add
                                    </Button>
                                </ColumnLayout>
                                {excludedMonths.length > 0 && (
                                    <TokenGroup
                                        items={excludedMonths.map(ym => ({ label: formatExcludedMonth(ym), dismissLabel: `Remove ${ym}` }))}
                                        onDismiss={({ detail }) => removeExcludedMonth(detail.itemIndex)}
                                    />
                                )}
                            </SpaceBetween>
                        </FormField>
                    </React.Fragment>
                )}
            </SpaceBetween>
        </Modal>
    );
}
